import React, { useState, useEffect } from 'react';
import { Search, MapPin, ThumbsUp, Filter, X, Phone, Mail } from 'lucide-react';
import TherapistView from './TherapistView';
import TherapistImage from './TherapistImage';
import { formatLabel, formatList } from './formatLabel';

export default function TherapistDirectory() {
  const [therapists, setTherapists] = useState([]);
  const [filteredTherapists, setFilteredTherapists] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInsurance, setSelectedInsurance] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    loadTherapists();
  }, []);

  useEffect(() => {
    filterTherapists();
  }, [searchQuery, selectedInsurance, selectedState, selectedSpecialty, therapists]);

  const loadTherapists = async () => {
    try {
      const response = await fetch('/directoryData.json');
      const jsonData = await response.json();

      const processedData = jsonData.map(row => {
        let insuranceList = [];
        try {
          if (row.insurance) {
            insuranceList = typeof row.insurance === 'string'
              ? JSON.parse(row.insurance)
              : row.insurance;
          }
        } catch (e) {
          insuranceList = [];
        }

        let statesList = [];
        try {
          if (row['states active']) {
            statesList = typeof row['states active'] === 'string'
              ? JSON.parse(row['states active'])
              : row['states active'];
          }
        } catch (e) {
          statesList = [];
        }

        let specialtiesList = [];
        try {
          if (row.Specialities) {
            specialtiesList = typeof row.Specialities === 'string'
              ? JSON.parse(row.Specialities)
              : row.Specialities;
          }
        } catch (e) {
          specialtiesList = [];
        }

        let officeAddress = null;
        try {
          if (row['Office address']) {
            officeAddress = typeof row['Office address'] === 'string'
              ? JSON.parse(row['Office address'])
              : row['Office address'];
          }
        } catch (e) {
          officeAddress = null;
        }

        const identityText = formatList(row['Identity ']);
        const languagesList = formatList(row['Languages spoken']);

        return {
          firstName: row['First name'] ? row['First name'].trim() : '',
          lastName: row['Last name'] ? row['Last name'].trim() : '',
          email: row.email,
          phone: row['phone number'],
          insurance: insuranceList,
          states: statesList,
          specialties: specialtiesList,
          officeAddress: officeAddress,
          acceptingNewPatients: row['Open to one-off/brief course'],
          licenseType: row['license type'] ? formatLabel(row['license type']) : '',
          identity: identityText,
          pronouns: row.Pronouns,
          languages: languagesList,
          price: row.price,
          sessionType: row['session type'],
          biography: row['Biography/intro'],
          modalities: row.Modalities,
          availableTimes: row['Available times'],
          certificateStatus: row['joyous certificate status (internal, not on jotform)'],
          endorsements: Math.floor(Math.random() * 10) + 1
        };
      });

      const sortedData = processedData.sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setTherapists(sortedData);

      const allInsurance = new Set();
      const allStates = new Set();
      const allSpecialties = new Set();
      sortedData.forEach(t => {
        t.insurance.forEach(ins => allInsurance.add(ins));
        t.states.forEach(state => allStates.add(state));
        t.specialties.forEach(spec => allSpecialties.add(spec));
      });

      const insuranceList = Array.from(allInsurance).sort().map(ins => ({
        value: ins,
        label: formatLabel(ins)
      }));

      const stateList = Array.from(allStates).sort();

      const specialtyList = Array.from(allSpecialties).sort().map(spec => ({
        value: spec,
        label: formatLabel(spec)
      }));

      setInsuranceOptions(insuranceList);
      setStateOptions(stateList);
      setSpecialtyOptions(specialtyList);
      setLoading(false);
    } catch (error) {
      console.error('Error loading therapists:', error);
      setLoading(false);
    }
  };

  const filterTherapists = () => {
    let filtered = therapists;

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(t => {
        const fullName = `${t.firstName} ${t.lastName}`.toLowerCase();
        return fullName.includes(query);
      });
    }

    if (selectedInsurance !== 'all') {
      filtered = filtered.filter(t =>
        t.insurance.includes(selectedInsurance)
      );
    }

    if (selectedState !== 'all') {
      filtered = filtered.filter(t =>
        t.states.includes(selectedState)
      );
    }

    if (selectedSpecialty !== 'all') {
      filtered = filtered.filter(t =>
        t.specialties.includes(selectedSpecialty)
      );
    }

    setFilteredTherapists(filtered);
  };

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const phoneStr = phone.toString();
    if (phoneStr.length === 10) {
      return `(${phoneStr.slice(0, 3)}) ${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`;
    }
    return phoneStr;
  };

  const getFullAddress = (address) => {
    if (!address) return null;
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.postal_code) parts.push(address.postal_code);
    return parts.join(', ');
  };

  const openModal = (therapist) => {
    setSelectedTherapist(therapist);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTherapist(null);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedInsurance('all');
    setSelectedState('all');
    setSelectedSpecialty('all');
  };

  const hasActiveFilters = searchQuery.trim() !== '' || selectedInsurance !== 'all' || selectedState !== 'all' || selectedSpecialty !== 'all';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading therapists...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-blue-600 text-white pt-6 pb-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6">
            <a href="https://joyous.team" rel="noopener noreferrer" className="mb-4 sm:mb-0">
              <svg width="48" height="48" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer hover:opacity-90 transition-opacity">
                <circle cx="16" cy="16" r="13" stroke="#9b87f5" strokeWidth="2" fill="none" />
                <path d="M 16 9 A 7 7 0 0 1 16 23"
                  stroke="#9b87f5"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  transform="rotate(45 16 16)" />
              </svg>
            </a>
            <div className="flex flex-col">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">Find a Therapist</h1>
              <p className="text-violet-100 text-sm sm:text-base">Connect with the right mental health professional for you</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-8">
        {/* Mobile Search + Filter Button */}
        <div className="md:hidden mb-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name…"
              className="w-full pl-10 pr-10 py-3 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm"
          >
            <Filter size={20} />
            <span className="font-medium">Filter Results</span>
            {hasActiveFilters && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                Active
              </span>
            )}
          </button>
        </div>

        {/* Desktop Filters */}
        <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Search className="text-gray-400" size={20} />
            <h2 className="text-lg font-semibold text-gray-700">Filter Results</h2>
          </div>

          <div className="mb-4">
            <label htmlFor="name-search" className="block text-sm font-medium text-gray-700 mb-2">
              Search by name
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                id="name-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type a therapist's name…"
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="insurance-filter" className="block text-sm font-medium text-gray-700 mb-2">
                Insurance
              </label>
              <select
                id="insurance-filter"
                value={selectedInsurance}
                onChange={(e) => setSelectedInsurance(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Insurance Types</option>
                {insuranceOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="state-filter" className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              <select
                id="state-filter"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All States</option>
                {stateOptions.map(state => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="specialty-filter" className="block text-sm font-medium text-gray-700 mb-2">
                Specialty
              </label>
              <select
                id="specialty-filter"
                value={selectedSpecialty}
                onChange={(e) => setSelectedSpecialty(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Specialties</option>
                {specialtyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredTherapists.length} therapist{filteredTherapists.length !== 1 ? 's' : ''}
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Mobile Filter Modal */}
        {mobileFiltersOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
            <div className="fixed inset-x-0 bottom-0 bg-white rounded-t-xl p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Filter Results</h2>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Insurance
                  </label>
                  <select
                    value={selectedInsurance}
                    onChange={(e) => setSelectedInsurance(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Insurance Types</option>
                    {insuranceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All States</option>
                    {stateOptions.map(state => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialty
                  </label>
                  <select
                    value={selectedSpecialty}
                    onChange={(e) => setSelectedSpecialty(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Specialties</option>
                    {specialtyOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-md font-medium"
                >
                  Show {filteredTherapists.length} Results
                </button>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-md font-medium"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Results count for mobile */}
        <div className="md:hidden mb-4 text-sm text-gray-600">
          Showing {filteredTherapists.length} therapist{filteredTherapists.length !== 1 ? 's' : ''}
        </div>

        {/* Therapist Cards */}
        <div className="space-y-4">
          {filteredTherapists.map((therapist, index) => (
            <div key={therapist.email || `${therapist.firstName}-${therapist.lastName}-${index}`} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-all">
              {/* Mobile Layout */}
              <div className="md:hidden">
                <div className="flex items-start gap-3 mb-4">
                  <TherapistImage
                    firstName={therapist.firstName}
                    lastName={therapist.lastName}
                  />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-blue-600 mb-1">
                      {therapist.firstName} {therapist.lastName}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {therapist.licenseType ? `${therapist.licenseType}, ` : ''}
                    </p>
                    {therapist.endorsements && (
                      <div className="flex items-center gap-1 mt-1">
                        <ThumbsUp className="w-3 h-3 text-gray-500" />
                        <span className="text-xs text-gray-600">{therapist.endorsements} Endorsed</span>
                      </div>
                    )}
                  </div>
                </div>

                {(therapist.identity || therapist.pronouns || therapist.languages) && (
                  <div className="text-sm text-gray-600 mb-3">
                    {therapist.identity && (
                      <div className="mb-1">
                        {therapist.identity.length > 80
                          ? therapist.identity.substring(0, 80) + '...'
                          : therapist.identity}
                      </div>
                    )}
                    {therapist.pronouns && (
                      <span className="mr-2">({therapist.pronouns.replace(/_/g, '/')})</span>
                    )}
                    {therapist.languages && (
                      <div>Languages: {therapist.languages}</div>
                    )}
                  </div>
                )}

                {therapist.officeAddress && getFullAddress(therapist.officeAddress) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>{getFullAddress(therapist.officeAddress)}</span>
                  </div>
                )}

                {therapist.states && therapist.states.length > 0 && (
                  <div className="mb-3 text-sm">
                    <span className="text-gray-600">Licensed in: </span>
                    <span className="font-medium text-gray-700">
                      {therapist.states.join(', ')}
                    </span>
                  </div>
                )}

                {therapist.biography && (
                  <p className="text-sm text-gray-700 leading-relaxed line-clamp-3 mb-4">
                    {therapist.biography}
                  </p>
                )}

                <div className="space-y-2">
                  {therapist.phone && (
                    <a
                      href={`tel:${therapist.phone}`}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-md font-medium"
                    >
                      <Phone size={16} />
                      {formatPhone(therapist.phone)}
                    </a>
                  )}
                  {therapist.email && (
                    <a
                      href={`mailto:${therapist.email}`}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-50 text-gray-700 rounded-md"
                    >
                      <Mail size={16} />
                      <span className="truncate">{therapist.email}</span>
                    </a>
                  )}
                  <button
                    onClick={() => openModal(therapist)}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:flex gap-6">
                <div className="flex-shrink-0">
                  <TherapistImage
                    firstName={therapist.firstName}
                    lastName={therapist.lastName}
                  />
                </div>

                <div className="flex-grow min-w-0 overflow-hidden">
                  <div className="mb-3">
                    <h2 className="text-2xl lg:text-3xl font-normal text-blue-600 mb-1">
                      {therapist.firstName} {therapist.lastName}
                    </h2>

                    <div className="flex items-center gap-4 text-gray-600 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-base">
                          {therapist.licenseType ? `${therapist.licenseType}, ` : ''}
                        </span>
                      </div>
                      {therapist.endorsements && (
                        <div className="flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4 text-gray-500" />
                          <span className="text-base">{therapist.endorsements} Endorsed</span>
                        </div>
                      )}
                    </div>

                    {(therapist.identity || therapist.pronouns || therapist.languages) && (
                      <div className="text-sm text-gray-600 mb-2 break-words overflow-hidden">
                        <div className="line-clamp-2">
                          {therapist.identity && (
                            <span className="mr-2">
                              {therapist.identity.length > 100
                                ? therapist.identity.substring(0, 100) + '...'
                                : therapist.identity}
                            </span>
                          )}
                          {therapist.pronouns && (
                            <span className="mr-2">({therapist.pronouns.replace(/_/g, '/')})</span>
                          )}
                          {therapist.languages && (
                            <span>• Languages: {therapist.languages}</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-6 text-gray-600 mb-3">
                      {therapist.officeAddress && getFullAddress(therapist.officeAddress) && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{getFullAddress(therapist.officeAddress)}</span>
                        </div>
                      )}
                    </div>

                    {therapist.states && therapist.states.length > 0 && (
                      <div className="mb-3">
                        <span className="text-sm text-gray-600 mr-2">Licensed in:</span>
                        <span className="text-sm font-medium text-gray-700">
                          {therapist.states.join(', ')}
                        </span>
                      </div>
                    )}

                    {therapist.biography && (
                      <p className="text-gray-700 leading-relaxed line-clamp-3">
                        {therapist.biography}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0 flex flex-col items-end gap-2 min-w-[200px]">
                  {therapist.phone && (
                    <div className="text-xl font-semibold text-gray-900">
                      {formatPhone(therapist.phone)}
                    </div>
                  )}

                  <button className="text-lg text-gray-700 hover:text-blue-600 transition-colors">
                    {therapist.email}
                  </button>

                  <button
                    onClick={() => openModal(therapist)}
                    className="px-8 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    View
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredTherapists.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 sm:p-12 text-center">
              <p className="text-gray-600 text-lg">No therapists found matching your criteria.</p>
              <p className="text-gray-500 mt-2">Try adjusting your filters to see more results.</p>
            </div>
          )}
        </div>
      </div>

      <TherapistView
        therapist={selectedTherapist}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
}
